import type { StateCreator } from 'zustand';
import { logError } from '../lib/logError';
import type { CustomAudiencesStore } from './useCustomAudiencesStore';

/** A brand's display name, keyed by the id the filters store. */
export type BrandName = { brandId: string; name: string };

/** Fetches the names of the given brand ids in one call; ids it does not return stay unnamed. */
export type BrandNamesLookup = (brandIds: string[]) => Promise<BrandName[]>;

export type BrandNamesSlice = {
  /** Brand names by brand id, from brand searches and batch lookups. */
  brandNames: Record<string, string>;
  /** Ids a batch lookup has been sent for, so each id is fetched once. */
  brandNamesRequested: Record<string, true>;

  /** Records names already in hand, such as brand search results. */
  rememberBrandNames: (brands: BrandName[]) => void;
  /**
   * Queues ids for a name lookup.
   *
   * Every id queued in the same tick goes out in one `lookup` call.
   * Ids already named or already requested are skipped.
   * A failed lookup is logged and its ids can be requested again, so their chips keep the raw id meanwhile.
   */
  requestBrandNames: (brandIds: string[], lookup: BrandNamesLookup) => void;
};

export const createBrandNamesSlice: StateCreator<CustomAudiencesStore, [], [], BrandNamesSlice> = (
  set,
  get
) => {
  /* Ids queued since the last flush, and the lookup the latest caller passed */
  let queued = new Set<string>();
  let queuedLookup: BrandNamesLookup | null = null;

  const flush = async () => {
    const lookup = queuedLookup;
    const { brandNames, brandNamesRequested } = get();
    const brandIds = [...queued].filter(id => !brandNames[id] && !brandNamesRequested[id]);
    queued = new Set();
    queuedLookup = null;
    if (!lookup || brandIds.length === 0) return;

    set(state => ({
      brandNamesRequested: {
        ...state.brandNamesRequested,
        ...Object.fromEntries(brandIds.map(id => [id, true as const]))
      }
    }));
    try {
      get().rememberBrandNames(await lookup(brandIds));
    } catch (error) {
      logError('brandNames', error);
      set(state => {
        const requested = { ...state.brandNamesRequested };
        for (const id of brandIds) delete requested[id];
        return { brandNamesRequested: requested };
      });
    }
  };

  return {
    brandNames: {},
    brandNamesRequested: {},

    rememberBrandNames: brands => {
      const named = brands.filter(brand => brand.brandId && brand.name);
      if (named.length === 0) return;
      set(state => ({
        brandNames: {
          ...state.brandNames,
          ...Object.fromEntries(named.map(brand => [brand.brandId, brand.name]))
        }
      }));
    },

    requestBrandNames: (brandIds, lookup) => {
      if (brandIds.length === 0) return;
      const first = queued.size === 0 && queuedLookup === null;
      for (const id of brandIds) queued.add(id);
      queuedLookup = lookup;
      /* A microtask, so every component that renders in the same commit joins one call */
      if (first)
        queueMicrotask(() => {
          flush();
        });
    }
  };
};
